import React from 'react';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductCard = ({ product, viewMode = 'grid' }) => {
  const handleViewDetails = () => {
    window.location.href = '/product-detail';
  };

  const getCertificationColor = (cert) => {
    switch (cert) {
      case 'FDA': return 'text-success bg-success/10 border-success/20';
      case 'CE': return 'text-primary bg-primary/10 border-primary/20';
      case 'GDPMD': return 'text-warning bg-warning/10 border-warning/20';
      default: return 'text-text-secondary bg-muted border-border';
    }
  };

  const getAvailabilityColor = (status) => {
    switch (status) {
      case 'In Stock': return 'text-success';
      case 'Pre-Order': return 'text-warning';
      case 'Custom Order': return 'text-primary';
      default: return 'text-text-secondary';
    }
  };

  if (viewMode === 'list') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -2 }}
        className="bg-card border border-border rounded-lg p-6 shadow-medical hover:shadow-medical-hover transition-all duration-300"
      >
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Product Image */}
          <div className="flex-shrink-0">
            <div className="w-full lg:w-48 h-48 bg-muted rounded-lg overflow-hidden">
              <Image
                src={product?.image}
                alt={product?.name}
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>

          {/* Product Details */}
          <div className="flex-1 space-y-4">
            <div>
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-xl font-semibold text-text-primary hover:text-primary transition-colors duration-200">
                  {product?.name}
                </h3>
                <span className="text-sm font-medium text-text-secondary bg-muted px-2 py-1 rounded">
                  {product?.category}
                </span>
              </div>
              <p className="text-text-secondary text-sm mb-3 line-clamp-2">
                {product?.description}
              </p>
            </div>

            {/* Specifications */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-xs font-medium text-text-secondary uppercase tracking-wide">Manufacturer</span>
                <p className="text-sm font-medium text-text-primary">{product?.manufacturer}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-text-secondary uppercase tracking-wide">Model</span>
                <p className="text-sm font-medium text-text-primary">{product?.model}</p>
              </div>
            </div>

            {/* Certifications & Availability */}
            <div className="flex flex-wrap items-center gap-3">
              {product?.certifications?.map((cert, index) => (
                <span
                  key={index}
                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${getCertificationColor(cert)}`}
                >
                  <Icon name="Shield" size={12} className="mr-1" />
                  {cert}
                </span>
              ))}
              <span className={`inline-flex items-center text-sm font-medium ${getAvailabilityColor(product?.availability)}`}>
                <Icon name="Package" size={14} className="mr-1" />
                {product?.availability}
              </span>
            </div>

            {/* Price & Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div>
                {product?.price && (
                  <span className="text-lg font-semibold text-text-primary">
                    RM {product?.price?.toLocaleString()}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Heart"
                  iconPosition="left"
                >
                  Save
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleViewDetails}
                  iconName="ArrowRight"
                  iconPosition="right"
                >
                  View Details
                </Button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      className="bg-card border border-border rounded-lg overflow-hidden shadow-medical hover:shadow-medical-hover transition-all duration-300 group"
    >
      {/* Product Image */}
      <div className="relative h-48 bg-muted overflow-hidden">
        <Image
          src={product?.image}
          alt={product?.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        
        {/* Overlay Actions */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              size="sm"
              iconName="Eye"
              iconPosition="left"
              onClick={handleViewDetails}
            >
              Quick View
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Heart"
            />
          </div>
        </div>

        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <span className="bg-primary text-primary-foreground text-xs font-medium px-2 py-1 rounded-full">
            {product?.category}
          </span>
        </div>

        {/* Availability Badge */}
        <div className="absolute top-3 right-3">
          <span className={`text-xs font-medium px-2 py-1 rounded-full bg-white/90 ${getAvailabilityColor(product?.availability)}`}>
            {product?.availability}
          </span>
        </div>
      </div>
      {/* Product Details */}
      <div className="p-4 space-y-3">
        <div>
          <h3 className="text-lg font-semibold text-text-primary line-clamp-1 group-hover:text-primary transition-colors duration-200">
            {product?.name}
          </h3>
          <p className="text-sm text-text-secondary mt-1 line-clamp-2">
            {product?.description}
          </p>
        </div>

        {/* Specifications */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">Manufacturer:</span>
            <span className="font-medium text-text-primary">{product?.manufacturer}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-text-secondary">Model:</span>
            <span className="font-medium text-text-primary">{product?.model}</span>
          </div>
        </div>

        {/* Certifications */}
        <div className="flex flex-wrap gap-2">
          {product?.certifications?.map((cert, index) => (
            <span
              key={index}
              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${getCertificationColor(cert)}`}
            >
              <Icon name="Shield" size={10} className="mr-1" />
              {cert}
            </span>
          ))}
        </div>

        {/* Price & Action */}
        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div>
            {product?.price && (
              <span className="text-lg font-semibold text-text-primary">
                RM {product?.price?.toLocaleString()}
              </span>
            )}
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={handleViewDetails}
            iconName="ArrowRight"
            iconPosition="right"
          >
            Details
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;